import React from 'react';
import {
  Database, Brain, Settings, FileText, Link as LinkIcon, AlertTriangle,
  Users, History, Lightbulb, Target, Smile, MessageSquare, Zap, Cpu, Power, Edit2, ShieldCheck // Added more icons
} from 'lucide-react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';

import { AgentData_V10, AgentHubPageViewMode, KnowledgeSourceV10, TLLMDisplayNameV10 } from '@dopomogai/types';
import { Card } from '@dopomogai/ui';
import {Button} from '@dopomogai/ui';
import { Badge } from '@dopomogai/ui';
import {
  INNER_CARD_ANIMATE_RESTING,
  INNER_CARD_HOVER,
  INNER_CARD_TRANSITION_HOVER,
} from '../../shared/animationConstants';

// --- Helper Functions (Owner View) ---
const getKnowledgeSourceIcon = (type: KnowledgeSourceV10['type']) => {
  switch (type) {
    case 'URL': return <LinkIcon size={16} className="text-blue-500" />;
    case 'File Upload': return <FileText size={16} className="text-green-500" />;
    case 'Database Connection': return <Database size={16} className="text-purple-500" />;
    case 'API Endpoint': return <Power size={16} className="text-orange-500" />;
    default: return <FileText size={16} className="text-gray-500" />;
  }
};
const getKnowledgeSourceStatusBadgeColor = (status: KnowledgeSourceV10['status']): 'green' | 'blue' | 'yellow' | 'red' | 'grey' => {
  switch (status) {
    case 'Connected': return 'green';
    case 'Syncing': return 'blue';
    case 'Error': return 'red';
    case 'Needs Attention': return 'yellow';
    default: return 'grey';
  }
};
const getLLMDisplayNameSimplified = (displayName: TLLMDisplayNameV10): string => {
    if (displayName.includes('Proprietary')) return 'Proprietary LLM';
    if (displayName.includes('GPT-4')) return 'GPT-4 Class';
    if (displayName.includes('Claude 3')) return 'Claude 3 Class';
    if (displayName.includes('Custom Fine-Tuned')) return 'Custom Model';
    return 'Platform Default';
};

// --- NEW PREMIUM PUBLIC VIEW COMPONENTS ---

interface CapabilityTileProps {
  icon: React.ReactNode;
  titleKey: string;
  descriptionKey: string;
  descriptionOptions?: object;
  tFunc: Function;
  className?: string;
}

const CapabilityTile: React.FC<CapabilityTileProps> = ({ icon, titleKey, descriptionKey, descriptionOptions, tFunc, className }) => {
  return (
    <motion.div
      animate={INNER_CARD_ANIMATE_RESTING}
      whileHover={INNER_CARD_HOVER}
      transition={INNER_CARD_TRANSITION_HOVER}
      className={clsx(
        "bg-white p-5 rounded-xl border-2 border-black flex flex-col items-start text-left h-full", // Added h-full for grid alignment
        className
      )}
    >
      <div className="mb-3 p-2.5 bg-sky-100 text-sky-600 rounded-lg border-2 border-black shadow-[2px_2px_0_0_black]">
        {icon}
      </div>
      <h4 className="text-md font-semibold text-gray-900 mb-1.5">{tFunc(titleKey)}</h4>
      <p className="text-sm text-gray-600 leading-relaxed">{tFunc(descriptionKey, descriptionOptions)}</p>
    </motion.div>
  );
};


// --- MAIN KNOWLEDGE SECTION COMPONENT ---
interface KnowledgeSectionProps {
  agentData: AgentData_V10;
  viewMode: AgentHubPageViewMode;
  onAction: (actionType: string, payload?: any) => void;
  headerBgColor?: string;
  id?: string;
}

export const KnowledgeSection = React.forwardRef<HTMLDivElement, KnowledgeSectionProps>(
  ({ agentData, viewMode, onAction, headerBgColor, id }, ref) => {
  const { t } = useTranslation('agent-common');
  const isOwnerView = viewMode === 'owner_dashboard';
  const { knowledgeManagementSettings, llmConfiguration, personaSettings } = agentData;

  // --- OWNER VIEW LOGIC (Placeholder - copy your full owner view logic here) ---
  if (isOwnerView) {
    // For brevity, using a placeholder.
    // Replace this with your full owner view JSX from the previous correct version.
    return (
      <Card id={id} ref={ref} titleKey="knowledgeSection.defaultTitle" icon={<Brain size={22} className="text-indigo-600" />} headerBgColor={headerBgColor}>
        <p className="text-center text-gray-400 italic py-8">Owner dashboard view for this section is not shown in this code snippet.</p>
      </Card>
    );
  }

  // --- PUBLIC VIEW LOGIC (Enhanced) ---
  if (!knowledgeManagementSettings || !llmConfiguration || !personaSettings) {
    return (
      <Card id={id} ref={ref} titleKey="knowledgeSection.defaultTitle" icon={<Brain size={22} className="text-indigo-600" />} headerBgColor={headerBgColor}>
        <p className="text-center text-gray-500 py-8">{t('common.loading')}</p>
      </Card>
    );
  }

  const simplifiedLlmName = getLLMDisplayNameSimplified(llmConfiguration.displayNamePublic);
  const modalitiesString = agentData.interactionModalitiesAvailable?.join(', ') || t('common.notAvailableAbbr');

  return (
    <Card 
      id={id} 
      ref={ref} 
      titleKey="knowledgeSection.defaultTitle" 
      icon={<Brain size={22} className="text-indigo-600" />} 
      headerBgColor={headerBgColor}
    >
      <div className="space-y-12 md:space-y-14">
        
        {/* Section 1: How It Learns & Adapts */}
        <section aria-labelledby="how-it-learns-heading">
          {/* REFINED Sub-header */}
          <div className="flex justify-center md:justify-start mb-4">
            <div id="how-it-learns-heading" className="inline-flex items-center gap-2.5 bg-teal-50 px-3 py-1.5 rounded-full border-2 border-black">
              <Database size={20} className="text-teal-600" />
              <h3 className="text-lg font-bold text-teal-800">{t('knowledgeSection.publicSubtitle.howItLearns')}</h3>
            </div>
          </div>
          
          <p className="text-gray-600 text-base md:text-lg leading-relaxed max-w-3xl mx-auto md:mx-0 mb-6 text-center md:text-left">
            {knowledgeManagementSettings.usesRAG 
              ? (knowledgeManagementSettings.ragDescription || t('knowledgeSection.ragActive.description'))
              : t('knowledgeSection.ragInactive.description')}
          </p>
          
          {knowledgeManagementSettings.usesRAG && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 max-w-3xl mx-auto md:mx-0">
              {/* FIXED: Added motion props to these tiles */}
              <CapabilityTile
                tFunc={t}
                icon={<History size={24} />}
                titleKey="knowledgeSection.capability.personalizedRecallTitle"
                descriptionKey="knowledgeSection.capability.personalizedRecallDesc"
              />
              <CapabilityTile
                tFunc={t}
                icon={<Target size={24} />}
                titleKey="knowledgeSection.capability.adaptiveContextTitle"
                descriptionKey="knowledgeSection.capability.adaptiveContextDesc"
              />
            </div>
          )}
        </section>

        {/* Subtle Divider */}
        <hr className="border-slate-200/80 my-6"/>

        {/* Section 2: The Mind of Your Agent */}
        <section aria-labelledby="how-it-thinks-heading">
          {/* REFINED Sub-header */}
          <div className="flex justify-center md:justify-start mb-4">
            <div id="how-it-thinks-heading" className="inline-flex items-center gap-2.5 bg-sky-50 px-3 py-1.5 rounded-full border-2 border-black">
              <Cpu size={20} className="text-sky-600" />
              <h3 className="text-lg font-bold text-sky-800">{t('knowledgeSection.publicSubtitle.howItThinks')}</h3>
            </div>
          </div>
          
          {/* REFINED "Powered By" Box */}
          <motion.div 
            animate={INNER_CARD_ANIMATE_RESTING}
            whileHover={INNER_CARD_HOVER}
            transition={INNER_CARD_TRANSITION_HOVER}
            className="flex flex-col sm:flex-row items-center gap-4 bg-white p-5 rounded-xl border-2 border-black mb-6 max-w-3xl mx-auto md:mx-0"
          >
            <div className="flex-shrink-0 text-sky-500 bg-sky-100 p-3 rounded-lg border-2 border-black shadow-sm">
              <Brain size={28} />
            </div>
            <p className="text-gray-700 text-base md:text-lg leading-relaxed text-center sm:text-left">
              {t('knowledgeSection.llmPoweredBy')} <span className="font-semibold text-sky-800">{simplifiedLlmName}</span>.
            </p>
          </motion.div>
          
          <div className="grid grid-cols-1 gap-5 max-w-3xl mx-auto md:mx-0">
            {/* This tile already had the correct animation */}
            <CapabilityTile
                tFunc={t}
                icon={<Smile size={24} />}
                titleKey="knowledgeSection.capability.engagingPersonaTitle"
                descriptionKey="knowledgeSection.capability.engagingPersonaDesc"
                descriptionOptions={{ 
                    personaName: personaSettings.overviewPublic || t('knowledgeSection.noPersonaDefined'),
                    modalities: modalitiesString 
                }}
            />
          </div>
        </section>
      </div>
    </Card>
  );
});

KnowledgeSection.displayName = "KnowledgeSection";
